import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, WifiOff } from 'lucide-react';
import { api } from '../../services/api';
import { PoS } from '../../types';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

interface SearchableCustomerSelectProps {
  onSelect: (customer: PoS) => void;
  onClear?: () => void;
  initialPosId?: string;
  locked?: boolean;
}

export const SearchableCustomerSelect: React.FC<SearchableCustomerSelectProps> = ({
  onSelect,
  onClear,
  initialPosId,
  locked = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PoS[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<PoS | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestion, setSuggestion] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    if (!initialPosId) return;
    let active = true;
    api
      .get(`/pos/${initialPosId}`)
      .then((res) => {
        if (!active) return;
        const pos = res.data as PoS;
        setSelectedCustomer(pos);
        setQuery(pos.name);
        onSelect(pos);
      })
      .catch(() => {
        if (!active) return;
        setSelectedCustomer(null);
        setQuery('');
      });
    return () => {
      active = false;
    };
  }, [initialPosId]);

  useEffect(() => {
    // Click outside handler
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const initialLoadDoneRef = useRef(false);
  const prevOnlineRef = useRef(isOnline);

  // Load initial PoS list on mount or when going back online
  useEffect(() => {
    const wentOnline = isOnline && !prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (!isOnline) return;
    if (locked) return;
    if (initialLoadDoneRef.current && !wentOnline) return;

    const loadInitialPoS = async () => {
      setLoading(true);
      try {
        const response = await api.get('/pos?limit=50');
        setResults(response.data.data || []);
        setTotalCount(response.data.total || 0);
        initialLoadDoneRef.current = true;
      } catch (error) {
        console.error('Failed to load PoS:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialPoS();
  }, [isOnline, locked]);

  useEffect(() => {
    const searchCustomers = async () => {
      // Don't search if customer is already selected or offline
      if (selectedCustomer) return;
      if (!isOnline) {
        // Clear results when offline
        setResults([]);
        setTotalCount(0);
        setSuggestion('');
        return;
      }
      
      // If query is empty, reload initial list
      if (query.length === 0) {
        setSuggestion('');
        setLoading(true);
        try {
          const response = await api.get('/pos?limit=50');
          setResults(response.data.data || []);
          setTotalCount(response.data.total || 0);
        } catch (error) {
          console.error('Failed to load PoS:', error);
        } finally {
          setLoading(false);
        }
        return;
      }

      // Search with 1+ characters
      setLoading(true);
      try {
        const response = await api.get(`/pos/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setTotalCount(response.data.length);
        setHighlightedIndex(0);
        
        // Generate autocomplete suggestion
        if (response.data.length > 0) {
          const firstMatch = response.data[0];
          const matchName = firstMatch.name.toLowerCase();
          const queryLower = query.toLowerCase();
          
          if (matchName.startsWith(queryLower)) {
            setSuggestion(firstMatch.name);
          } else {
            setSuggestion('');
          }
        } else {
          setSuggestion('');
        }
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
        setTotalCount(0);
        setSuggestion('');
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [query, selectedCustomer, isOnline]);

  const handleSelect = (customer: PoS) => {
    setSelectedCustomer(customer);
    setQuery(customer.name);
    setIsOpen(false);
    onSelect(customer);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        // Update suggestion to match highlighted item
        if (results[highlightedIndex + 1]) {
          const nextMatch = results[highlightedIndex + 1];
          if (nextMatch.name.toLowerCase().startsWith(query.toLowerCase())) {
            setSuggestion(nextMatch.name);
          }
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        // Update suggestion to match highlighted item
        if (results[highlightedIndex - 1]) {
          const prevMatch = results[highlightedIndex - 1];
          if (prevMatch.name.toLowerCase().startsWith(query.toLowerCase())) {
            setSuggestion(prevMatch.name);
          }
        }
        break;
      case 'ArrowRight':
      case 'End':
        // Accept the inline suggestion
        if (suggestion && suggestion !== query) {
          e.preventDefault();
          setQuery(suggestion);
          setSuggestion('');
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSuggestion('');
        break;
      case 'Tab':
        // Accept suggestion or select highlighted item
        if (suggestion && suggestion !== query) {
          e.preventDefault();
          setQuery(suggestion);
          setSuggestion('');
        } else if (results[highlightedIndex]) {
          e.preventDefault();
          handleSelect(results[highlightedIndex]);
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedCustomer(null);
    onClear?.();
    setIsOpen(true);
    
    // Clear suggestion if user is deleting
    if (value.length < query.length) {
      setSuggestion('');
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">Search PoS</label>
      
      {/* Offline notice */}
      {!isOnline && (
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <WifiOff className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">Internet connection required</p>
            <p className="text-xs mt-1">Please connect to search for Points of Sale. You can fill out the rest of the form offline.</p>
          </div>
        </div>
      )}
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? <Loader2 className="animate-spin h-5 w-5 text-gray-400" /> : <Search className="h-5 w-5 text-gray-400" />}
        </div>
        
        {/* Inline suggestion overlay */}
        {suggestion && suggestion !== query && (
          <div className="absolute inset-0 pl-10 pr-3 py-2 pointer-events-none">
            <div className="text-gray-400 sm:text-sm leading-5">
              <span className="invisible">{query}</span>
              <span>{suggestion.slice(query.length)}</span>
            </div>
          </div>
        )}
        
        <input
          ref={inputRef}
          type="text"
          disabled={!isOnline || locked}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 sm:text-sm relative z-10 disabled:bg-gray-100 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'transparent' }}
          placeholder={isOnline ? "Type to search or browse all..." : "Requires internet connection"}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (locked) return;
            setIsOpen(true);
            if (selectedCustomer) {
              setQuery('');
              setSelectedCustomer(null);
              setSuggestion('');
              onClear?.();
            }
          }}
          autoComplete="off"
        />
        
        {/* White background for input */}
        <div className="absolute inset-0 bg-white border border-gray-300 rounded-md -z-10"></div>
      </div>

      {isOpen && !locked && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto overscroll-contain touch-pan-y focus:outline-none sm:text-sm">
          {results.length > 0 ? (
            <>
              {/* Result count */}
              <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200">
                {query.length > 0 ? (
                  `${results.length} result${results.length !== 1 ? 's' : ''} found`
                ) : (
                  `Showing ${results.length} of ${totalCount} PoS`
                )}
              </div>
              
              {results.map((customer, index) => (
                <div
                  key={customer.id}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 transition-colors ${
                    index === highlightedIndex ? 'bg-brand-50' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleSelect(customer)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center">
                    <span className="font-normal ml-3 block truncate">
                      <span className="font-medium text-gray-900">{customer.name}</span>
                      <span className="text-gray-500 mx-2">-</span>
                      <span className="text-gray-500">{customer.address}, {customer.city}</span>
                      {customer.store_id && <span className="ml-2 text-xs text-gray-400">({customer.store_id})</span>}
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="cursor-default select-none relative py-4 px-3 text-center">
              {loading ? (
                <span className="text-gray-500">Loading...</span>
              ) : (
                <span className="text-gray-500">No PoS found. Try a different search term.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

